import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimes } from "react-icons/fa";

/**
 * ReusableModal — A consistent, centered modal layout for the entire app.
 *
 * Props:
 *   open        (bool)     — whether the modal is visible
 *   onClose     (func)     — called when backdrop / close button / Escape is pressed
 *   title       (string)   — modal header title
 *   subtitle    (string)   — optional subtitle below the title
 *   icon        (Component)— optional react-icons component for the header
 *   iconColor   (string)   — icon color (default "#6366f1")
 *   iconBg      (string)   — icon background (default "rgba(99,102,241,0.15)")
 *   width       (string)   — CSS width (default "min(95vw, 850px)")
 *   maxHeight   (string)   — CSS max-height for the entire modal (default "85vh")
 *   children    (node)     — scrollable body content
 */
const ReusableModal = ({
  open,
  onClose,
  title,
  subtitle,
  icon: Icon,
  iconColor = "#6366f1",
  iconBg = "rgba(99,102,241,0.15)",
  width = "min(95vw, 850px)",
  maxHeight = "85vh",
  children,
}) => {
  // Scroll lock + Escape key
  useEffect(() => {
    if (!open) return undefined;

    const body = document.body;
    const html = document.documentElement;

    // Capture original inline styles so we can restore them precisely
    const original = {
      bodyOverflow: body.style.overflow,
      bodyPaddingRight: body.style.paddingRight,
      htmlOverflow: html.style.overflow,
    };

    // Compensate for the scrollbar width so the layout doesn't shift when we
    // hide the scrollbar.
    const scrollBarWidth = window.innerWidth - html.clientWidth;

    body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    if (scrollBarWidth > 0) {
      body.style.paddingRight = `${scrollBarWidth}px`;
    }

    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);

    return () => {
      body.style.overflow = original.bodyOverflow;
      body.style.paddingRight = original.bodyPaddingRight;
      html.style.overflow = original.htmlOverflow;
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="reusable-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.45)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
            overscrollBehavior: "contain",
          }}
          onClick={onClose}
          onWheel={(e) => {
            // Prevent the underlying page from scrolling when the cursor
            // is over the dim/blur backdrop (some browsers still propagate
            // wheel events past `overflow:hidden`).
            if (e.target === e.currentTarget) e.preventDefault();
          }}
          onTouchMove={(e) => {
            if (e.target === e.currentTarget) e.preventDefault();
          }}
        >
          <motion.div
            key="reusable-modal-content"
            initial={{ opacity: 0, scale: 0.92, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 16 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            style={{
              background: "var(--color-bg-card)",
              borderRadius: 12,
              width,
              maxHeight,
              overflow: "hidden",
              boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
              border: "1px solid var(--color-border)",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                padding: "18px 24px",
                borderBottom: "1px solid var(--color-border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexShrink: 0,
                background: (() => {
                  const isDark = document.body.classList.contains("dark-mode");
                  return isDark
                    ? "linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(59,130,246,0.1) 100%)"
                    : "linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(59,130,246,0.05) 100%)";
                })(),
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {Icon && (
                  <div
                    style={{
                      background: iconBg,
                      padding: 8,
                      borderRadius: 10,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon size={18} color={iconColor} />
                  </div>
                )}
                <div>
                  <h5
                    className="fw-bold mb-0"
                    style={{ fontSize: 16, color: "var(--color-text)" }}
                  >
                    {title}
                  </h5>
                  {subtitle && (
                    <p
                      className="mb-0"
                      style={{ fontSize: 11, color: "var(--color-text-light)" }}
                    >
                      {subtitle}
                    </p>
                  )}
                </div>
              </div>

              <button
                onClick={onClose}
                style={{
                  background: "rgba(239,68,68,0.1)",
                  border: "1px solid rgba(239,68,68,0.3)",
                  borderRadius: 8,
                  padding: "6px 12px",
                  cursor: "pointer",
                  color: "#ef4444",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 13,
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(239,68,68,0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(239,68,68,0.1)";
                }}
              >
                <FaTimes size={12} /> Close
              </button>
            </div>

            {/* Scrollable Body */}
            <div style={{ flex: 1, overflowY: "auto" }}>{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReusableModal;
